import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { icWarningPopup } from "../../../../components/image/image-src";
import { OkCancelPopup } from "../../../../components/modal/OkCancelPopup";
import BookingService from "../../../../services/bookingService";
import Utils from "../../../../utils/utils";
import RefundFormModal from "./RefundFormModal";

interface RefundModalProps {
  ids: (number | string)[];
  bookingId: string;
  isOpen?: boolean;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onClose?: () => void;
  changeRequest?: ChangeRequestDTO;
}

const RefundModal: React.FC<RefundModalProps> = ({
  ids,
  changeRequest,
  bookingId,
  isOpen,
  onClose,
  onSubmit,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const title = t("Are you sure you want to cancel") + "?";
  const [receiveSuccess, setReceiveSuccess] = useState(false);
  const mutation = useMutation(
    async (cancelBookingForm: CancelBookingFormDTO) => {
      if (changeRequest) {
        return await BookingService.changeRequest({
          ...changeRequest,
          cancelBookingRequest: {
            ...changeRequest.cancelBookingRequest,
            cancelBookingForm,
          },
        });
      }
      return await BookingService.refundBooking({
        bookingId,
        bookingDetailIds: ids,
        cancelBookingForm,
      });
    },
    {
      onSuccess: (data) => {
        setReceiveSuccess(true);
        onSuccess && onSuccess();
      },
      onError: (error: any) => {
        if (error?.code === 2415) {
          alert(t("Not enough availability"));
        } else {
          alert("Error: " + error);
        }
      },
    },
  );

  const {
    data,
    isLoading: queryIsLoading,
    refetch,
    error,
  } = useQuery<any | undefined, Error>(
    ["receiveTicketForm", bookingId],
    async () => {
      return await BookingService.refundContent(bookingId, title, t);
    },
    {
      enabled: isOpen,
      onSuccess: (result) => {},
    },
  );

  const handleSubmit = (data: any) => {
    mutation.mutate(data);
  };

  const [fillInfo, setFillInfo] = useState(false);

  const onConfirm = () => {
    // handleSubmit();

    setFillInfo(true);
  };

  const handleOk = () => {
    setReceiveSuccess(false);
    onSubmit && onSubmit();
  };
  const handleClose = () => {
    onClose && onClose();
  };

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, bookingId, refetch]);

  if (queryIsLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative w-full max-w-3xl rounded-[1rem] border border-[#D7DCE3] bg-white shadow-lg">
          <div className="p-6"></div>
        </div>
      </div>
    );
  }

  if (!!changeRequest || (!receiveSuccess && fillInfo)) {
    return (
      <RefundFormModal
        onClose={() => {
          handleClose();
        }}
        onSubmit={(data) => {
          console.log(data);
          handleSubmit(data);
        }}
      />
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative w-full max-w-3xl rounded-[1rem] border border-[#D7DCE3] bg-white shadow-lg">
          <div className="p-6">
            {t("Error: ")}
            {error.message}
          </div>
        </div>
      </div>
    );
  }

  const renderModal = () => {
    if (receiveSuccess) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-[22.5rem] max-w-3xl rounded-[1rem] border border-[#D7DCE3] bg-white shadow-lg">
            <div className="p-6 text-center">
              <img
                src="../../../../assets/icons/success-icon.svg"
                className="mx-auto mb-6 h-12 w-12"
                alt=""
              />
              <h2 className="mb-2 text-[1.125rem] font-semibold leading-[1.6875rem] text-[#232323]">
                {t("Your request has been received")}
              </h2>
              <p className="mb-6 text-[0.875rem] font-normal leading-[1.375rem] text-[#5E6D77]">
                {t(
                  "Your downgrade request has been successfully submitted. Refunds will be processed according to the cancellation and refund policy, and you will be notified via email once completed",
                )}
              </p>

              <button
                onClick={handleOk}
                className={`${
                  !mutation.isLoading
                    ? "bg-primary text-[#232323]"
                    : "bg-[#DCDCDC] text-white"
                } w-full rounded-[3.4375rem] py-[0.8125rem] text-base font-semibold leading-[1.25rem] text-[#232323]`}
              >
                {t("Ok")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <OkCancelPopup
        title={data?.title}
        ic={icWarningPopup}
        onCancel={handleClose}
        onOk={onConfirm}
        okText={t("Confirm")}
        isLoading={mutation.isLoading}
        footerText={"*" + t("Check the cancellation and refund policy")}
      >
        <div
          className="text-[0.875rem] font-normal leading-[1.375rem] text-[#5E6D77]"
          dangerouslySetInnerHTML={Utils.createMarkup(data?.content)}
        />
      </OkCancelPopup>
    );
  };

  return <>{isOpen && renderModal()}</>;
};

export default RefundModal;
